import { object, number } from 'yup';
import { useFormikContext as useDrawerFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  mapIsoStringtoUtcObject,
  mapRepliconDateToUtcObject,
  toRepliconDate
} from '~/modules/common/dates/convert';
import {
  getProjectAvailableHours,
  getInitialEstimatedHoursForTaskAllocation
} from '../../common/taskAllocationEditorUtil';
import useOnSubmit from '../../common/useOnSubmit';

export const getDateRangeForNewTaskAllocation = ({
  taskStartDate,
  taskEndDate,
  resourceAllocationStartDate,
  resourceAllocationEndDate
}) => {
  const taskStart = taskStartDate && mapRepliconDateToUtcObject(taskStartDate);
  const taskEnd = taskEndDate && mapRepliconDateToUtcObject(taskEndDate);

  const allocationStart = mapIsoStringtoUtcObject(resourceAllocationStartDate);
  const allocationEnd = mapIsoStringtoUtcObject(resourceAllocationEndDate);

  const startDate =
    taskStart &&
    (allocationStart < taskStart || (taskEnd && allocationStart > taskEnd))
      ? taskStart
      : allocationStart;

  const endDate =
    taskEnd &&
    (allocationEnd > taskEnd || (taskStart && allocationEnd < taskStart))
      ? taskEnd
      : allocationEnd;

  return {
    startDate,
    endDate: startDate > endDate ? null : endDate
  };
};

export const getInitialValues = ({
  initialEstimatedHours,
  otherTaskAllocationsSummaryScheduleRules,
  resourceAllocationEndDate,
  resourceAllocationScheduleRules,
  resourceAllocationStartDate,
  taskEndDate,
  taskResourceUserAllocation,
  taskStartDate
}) => {
  const { startDate, endDate } = taskResourceUserAllocation
    ? {
        startDate: mapIsoStringtoUtcObject(
          taskResourceUserAllocation.startDate
        ),
        endDate: mapIsoStringtoUtcObject(taskResourceUserAllocation.endDate)
      }
    : getDateRangeForNewTaskAllocation({
        taskStartDate,
        taskEndDate,
        resourceAllocationStartDate,
        resourceAllocationEndDate
      });

  const projectAvailableHours = getProjectAvailableHours({
    start: startDate,
    end: endDate,
    resourceAllocationScheduleRules,
    otherTaskAllocationsSummaryScheduleRules
  });

  const allocatedHours = taskResourceUserAllocation
    ? taskResourceUserAllocation.totalHours
    : getInitialEstimatedHoursForTaskAllocation({
        initialEstimatedHours,
        projectAvailableHours
      });

  return {
    startDate: toRepliconDate(startDate),
    endDate: toRepliconDate(endDate),
    projectAvailableHours,
    allocatedHours: startDate && endDate ? allocatedHours : null
  };
};

export const buildValidationSchema = ({ formatMessage }) =>
  object().shape({
    startDate: object()
      .nullable()
      .required(
        formatMessage({
          id: 'taskAllocationEditor.pleaseSelectStartDate'
        })
      ),
    endDate: object()
      .nullable()
      .required(
        formatMessage({
          id: 'taskAllocationEditor.pleaseSelectEndDate'
        })
      ),
    allocatedHours: number()
      .required()
      .moreThan(0)
  });

const useFormState = ({
  onClose,
  projectId,
  taskId,
  userId,
  roleId,
  resourceEstimateId,
  taskStartDate,
  taskEndDate,
  otherTaskAllocationsSummary,
  resourceAllocation,
  initialEstimatedHours,
  taskResourceUserAllocation
}) => {
  const { tenantSlug } = useMeContext();

  const { formatMessage } = useIntl();
  const {
    setFieldValue,
    values: { resourceEstimates }
  } = useDrawerFormikContext();
  const {
    scheduleRules: resourceAllocationScheduleRules,
    startDate: resourceAllocationStartDate,
    endDate: resourceAllocationEndDate
  } = resourceAllocation;
  const {
    scheduleRules: otherTaskAllocationsSummaryScheduleRules
  } = otherTaskAllocationsSummary;

  const handleSubmit = useOnSubmit({
    setFieldValue,
    resourceEstimates
  });

  const onSubmit = async (values, { setSubmitting }) => {
    await handleSubmit({
      setSubmitting,
      projectId,
      taskId,
      userId,
      resourceEstimateId,
      tenantSlug,
      roleId,
      values,
      taskResourceUserAllocation
    });
    onClose();
  };

  return {
    initialValues: getInitialValues({
      initialEstimatedHours,
      otherTaskAllocationsSummaryScheduleRules,
      resourceAllocationEndDate,
      resourceAllocationScheduleRules,
      resourceAllocationStartDate,
      taskEndDate,
      taskResourceUserAllocation,
      taskStartDate
    }),
    validationSchema: buildValidationSchema({ formatMessage }),
    onSubmit
  };
};

export default useFormState;
